import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Settings, Mail, Bot, Shield } from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';

import SmtpTab from './admin-settings/SmtpTab';
import SiteTab from './admin-settings/SiteTab';
import TelegramTab from './admin-settings/TelegramTab';
import SendgridTab from './admin-settings/SendgridTab';
import SecurityTab from './admin-settings/SecurityTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AdminSettings orchestrator — holds all state and API calls,
 * delegates rendering to per-tab components in /pages/admin-settings/.
 */
const AdminSettings = () => {
  const [activeTab, setActiveTab] = useState('smtp');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const { authenticated, currentUser, loading: authLoading, getAuthHeader, recordAdminActivity } = useAdminAuth();
  const navigate = useNavigate();

  const [smtpSettings, setSmtpSettings] = useState({
    host: '', port: 587, username: '', password: '',
    from_email: '', from_name: 'Daye Farm Ltd', use_tls: true, use_ssl: false,
  });

  const [siteSettings, setSiteSettings] = useState({
    whatsapp_number: '', contact_email: 'sale@dayefarmltd.com', contact_phone: '',
    address: '', map_embed_url: '', map_coordinates: { lat: 0, lng: 0 },
  });

  const [testEmail, setTestEmail] = useState('');

  const [telegramSettings, setTelegramSettings] = useState({ bot_token: '', chat_id: '', enabled: true });
  const [testingTelegram, setTestingTelegram] = useState(false);

  const [sendgridSettings, setSendgridSettings] = useState({
    api_key: '', from_email: 'noreply@dayefarmltd.com', from_name: 'Daye Farm Ltd', enabled: true,
  });
  const [sendgridTestEmail, setSendgridTestEmail] = useState('');
  const [testingSendgrid, setTestingSendgrid] = useState(false);
  const [savingSendgrid, setSavingSendgrid] = useState(false);

  const [twoFA, setTwoFA] = useState({ enabled: false, loading: true });
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [settingUp2FA, setSettingUp2FA] = useState(false);

  const [passwordForm, setPasswordForm] = useState({ current: '', new_password: '', confirm: '' });
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    if (!authLoading && !authenticated) navigate('/admin/login');
  }, [authenticated, authLoading, navigate]);

  useEffect(() => {
    if (authenticated && currentUser) {
      if (currentUser.role !== 'super_admin') {
        toast.error('Only super admin can access settings');
        navigate('/admin/dashboard');
        return;
      }
      fetchSettings();
      fetch2FAStatus();
    }
  }, [authenticated, currentUser]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const [smtpRes, siteRes, telegramRes, sendgridRes] = await Promise.all([
        axios.get(`${API}/admin/settings/smtp`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/settings/site`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/settings/telegram`, { headers: getAuthHeader() }).catch(() => ({ data: { settings: {} } })),
        axios.get(`${API}/admin/settings/sendgrid`, { headers: getAuthHeader() }).catch(() => ({ data: { settings: {} } })),
      ]);
      if (smtpRes.data.settings) setSmtpSettings((prev) => ({ ...prev, ...smtpRes.data.settings }));
      if (siteRes.data.settings) setSiteSettings((prev) => ({ ...prev, ...siteRes.data.settings }));
      if (telegramRes.data.settings && Object.keys(telegramRes.data.settings).length > 0) {
        setTelegramSettings((prev) => ({ ...prev, ...telegramRes.data.settings }));
      }
      if (sendgridRes.data.settings && Object.keys(sendgridRes.data.settings).length > 0) {
        setSendgridSettings((prev) => ({ ...prev, ...sendgridRes.data.settings }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSmtpChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSmtpSettings((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (name === 'port' ? parseInt(value) || 0 : value),
    }));
  };

  const handleSiteChange = (e) => {
    const { name, value } = e.target;
    setSiteSettings((prev) => ({ ...prev, [name]: value }));
  };

  const saveSmtpSettings = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/settings/smtp`, smtpSettings, { headers: getAuthHeader() });
      toast.success('SMTP settings saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save SMTP settings');
    } finally { setSaving(false); }
  };

  const saveSiteSettings = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/settings/site`, siteSettings, { headers: getAuthHeader() });
      toast.success('Site settings saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save site settings');
    } finally { setSaving(false); }
  };

  const testSmtpConnection = async () => {
    if (!testEmail) { toast.error('Please enter a test email address'); return; }
    setTesting(true);
    try {
      await axios.post(`${API}/admin/settings/smtp/test`, { email: testEmail }, { headers: getAuthHeader() });
      toast.success(`Test email sent to ${testEmail}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally { setTesting(false); }
  };

  const handleTelegramChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTelegramSettings((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const saveTelegramSettings = async () => {
    setSaving(true);
    try {
      const payload = { ...telegramSettings };
      if (payload.bot_token && payload.chat_id) payload.enabled = true;
      await axios.put(`${API}/admin/settings/telegram`, payload, { headers: getAuthHeader() });
      setTelegramSettings(payload);
      toast.success('Telegram settings saved and enabled');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save Telegram settings');
    } finally { setSaving(false); }
  };

  const testTelegramNotification = async () => {
    setTestingTelegram(true);
    try {
      const res = await axios.post(`${API}/admin/settings/telegram/test`, {}, { headers: getAuthHeader() });
      if (res.data.success) toast.success('Test notification sent to Telegram successfully!');
      else toast.error(res.data.message || 'Failed to send notification. Check your credentials.');
    } catch (error) {
      toast.error(error.response?.data?.detail || error.response?.data?.message || 'Failed to send test notification');
    } finally { setTestingTelegram(false); }
  };

  const saveSendgridSettings = async () => {
    recordAdminActivity();
    setSavingSendgrid(true);
    try {
      const response = await axios.put(`${API}/admin/settings/sendgrid`, sendgridSettings, { headers: getAuthHeader() });
      setSendgridSettings((previous) => ({
        ...previous,
        api_key: '',
        api_key_masked: response.data.api_key_masked || previous.api_key_masked,
      }));
      toast.success('SendGrid settings saved');
    } catch (error) {
      toast.error('Failed to save SendGrid settings');
    } finally { setSavingSendgrid(false); }
  };

  const testSendgridEmail = async () => {
    if (!sendgridTestEmail) { toast.error('Enter a test email address'); return; }
    recordAdminActivity();
    setTestingSendgrid(true);
    try {
      const res = await axios.post(`${API}/admin/settings/sendgrid/test`, { email: sendgridTestEmail }, { headers: getAuthHeader() });
      toast.success(res.data.message || 'Test email sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally { setTestingSendgrid(false); }
  };

  const fetch2FAStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/2fa/status`, { headers: getAuthHeader() });
      setTwoFA({ enabled: res.data.enabled, loading: false });
    } catch {
      setTwoFA({ enabled: false, loading: false });
    }
  };

  const setup2FA = async () => {
    setSettingUp2FA(true);
    try {
      const res = await axios.post(`${API}/admin/2fa/setup`, {}, { headers: getAuthHeader() });
      if (res.data.success) setSetupData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to setup 2FA');
    } finally { setSettingUp2FA(false); }
  };

  const verifySetup2FA = async () => {
    if (!verifyCode || verifyCode.length !== 6) { toast.error('Enter a valid 6-digit code'); return; }
    try {
      const res = await axios.post(`${API}/admin/2fa/verify-setup`, { code: verifyCode }, { headers: getAuthHeader() });
      if (res.data.success) {
        toast.success('2FA enabled successfully!');
        setTwoFA({ enabled: true, loading: false });
        setSetupData(null);
        setVerifyCode('');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    }
  };

  const disable2FA = async () => {
    if (!disableCode || disableCode.length !== 6) { toast.error('Enter a valid 6-digit code to disable'); return; }
    try {
      const res = await axios.post(`${API}/admin/2fa/disable`, { code: disableCode }, { headers: getAuthHeader() });
      if (res.data.success) {
        toast.success('2FA disabled');
        setTwoFA({ enabled: false, loading: false });
        setDisableCode('');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    }
  };

  const changePassword = async () => {
    if (!passwordForm.current || !passwordForm.new_password) return toast.error('All fields are required');
    if (passwordForm.new_password.length < 8) return toast.error('Password must be at least 8 characters');
    if (passwordForm.new_password !== passwordForm.confirm) return toast.error('Passwords do not match');
    setChangingPassword(true);
    try {
      await axios.put(`${API}/admin/change-password`, {
        current_password: passwordForm.current,
        new_password: passwordForm.new_password,
      }, { headers: getAuthHeader() });
      toast.success('Password changed successfully');
      setPasswordForm({ current: '', new_password: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally { setChangingPassword(false); }
  };

  if (authLoading || !authenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-settings-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Settings</h1>
        <p className="text-sm text-gray-500">Configure email, notifications, and site settings</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6 flex-wrap">
        <Button variant={activeTab === 'smtp' ? 'default' : 'outline'} onClick={() => setActiveTab('smtp')} data-testid="smtp-tab-btn">
          <Mail className="h-4 w-4 mr-2" /> SMTP Settings
        </Button>
        <Button variant={activeTab === 'site' ? 'default' : 'outline'} onClick={() => setActiveTab('site')} data-testid="site-tab-btn">
          <Settings className="h-4 w-4 mr-2" /> Site Settings
        </Button>
        <Button variant={activeTab === 'telegram' ? 'default' : 'outline'} onClick={() => setActiveTab('telegram')} data-testid="telegram-tab-btn">
          <Bot className="h-4 w-4 mr-2" /> Telegram
        </Button>
        <Button variant={activeTab === 'sendgrid' ? 'default' : 'outline'} onClick={() => setActiveTab('sendgrid')} data-testid="sendgrid-tab-btn">
          <Mail className="h-4 w-4 mr-2" /> SendGrid
        </Button>
        <Button variant={activeTab === 'security' ? 'default' : 'outline'} onClick={() => setActiveTab('security')} data-testid="security-tab-btn">
          <Shield className="h-4 w-4 mr-2" /> Security
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
        </div>
      ) : (
        <>
          {activeTab === 'smtp' && (
            <SmtpTab
              smtpSettings={smtpSettings}
              onSmtpChange={handleSmtpChange}
              onSave={saveSmtpSettings}
              saving={saving}
              testEmail={testEmail}
              setTestEmail={setTestEmail}
              onTest={testSmtpConnection}
              testing={testing}
            />
          )}
          {activeTab === 'site' && (
            <SiteTab siteSettings={siteSettings} onSiteChange={handleSiteChange} onSave={saveSiteSettings} saving={saving} />
          )}
          {activeTab === 'telegram' && (
            <TelegramTab
              telegramSettings={telegramSettings}
              onTelegramChange={handleTelegramChange}
              onSave={saveTelegramSettings}
              saving={saving}
              onTest={testTelegramNotification}
              testingTelegram={testingTelegram}
            />
          )}
          {activeTab === 'sendgrid' && (
            <SendgridTab
              sendgridSettings={sendgridSettings}
              setSendgridSettings={setSendgridSettings}
              onSave={saveSendgridSettings}
              savingSendgrid={savingSendgrid}
              sendgridTestEmail={sendgridTestEmail}
              setSendgridTestEmail={setSendgridTestEmail}
              onTest={testSendgridEmail}
              testingSendgrid={testingSendgrid}
            />
          )}
          {activeTab === 'security' && (
            <SecurityTab
              twoFA={twoFA}
              setupData={setupData}
              setSetupData={setSetupData}
              verifyCode={verifyCode}
              setVerifyCode={setVerifyCode}
              disableCode={disableCode}
              setDisableCode={setDisableCode}
              onSetup2FA={setup2FA}
              onVerifySetup2FA={verifySetup2FA}
              onDisable2FA={disable2FA}
              settingUp2FA={settingUp2FA}
              passwordForm={passwordForm}
              setPasswordForm={setPasswordForm}
              onChangePassword={changePassword}
              changingPassword={changingPassword}
            />
          )}
        </>
      )}
    </div>
  );
};

export default AdminSettings;
